import Icon from "@react-native-vector-icons/material-design-icons";
import theme from "assets/theme";
import React, { useState } from "react";
import {
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import { TABLE_PAGE_SIZES } from "web/constants";
import CustomButton from "webComponentsFormElements/inputs/CustomButton.component";
import Dropdown from "webComponentsFormElements/inputs/Dropdown.component";

const classes = StyleSheet.create({
  root: {
    borderWidth: 1,
    borderColor: theme.color.grey,
    borderRadius: 10,
    backgroundColor: theme.color.background,
    width: "100%",
    maxWidth: "100%",
    height: 750,
    overflow: "hidden",
  },

  tableHeaderRow: {
    flexDirection: "row",
    backgroundColor: theme.color.tableHeader,
  },
  tableHeaderCell: {
    padding: 10,
    backgroundColor: theme.color.tableHeader,
    minWidth: 0,
  },
  tableHeaderCellText: {
    fontSize: 16,
    color: theme.color.background,
    fontWeight: "bold",
  },
  tableBodyRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: theme.color.grey,
    alignItems: "center",
  },
  tableBodyCell: {
    flexDirection: "row",
    paddingVertical: 0,
    paddingHorizontal: 15,
    borderRightWidth: 1,
    borderRightColor: theme.color.grey,
    alignItems: "center",
    minWidth: 0,
    flex: 1, // Use flex for consistent sizing
    alignSelf: "stretch",
    overflow: "hidden",
  },
  tableBodyCellTextContainer: {
    maxWidth: "100%",
  },
  tableBodyCellText: {
    color: theme.color.darkGrey,
    textWrap: "wrap",
  },
  tableFooterRow: {
    flexDirection: "row",
    backgroundColor: theme.color.tableHeader,
    borderTopWidth: 1,
    borderTopColor: theme.color.grey,
    borderBottomLeftRadius: 10,
    borderBottomRightRadius: 10,
  },
  tools: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: theme.color.tableHeader,
    paddingLeft: 10,
    zIndex: 10,
  },
  tableFooterCell: {
    padding: 10,
    borderRightWidth: 1,
    borderRightColor: theme.color.grey,
    justifyContent: "center",
    alignItems: "center",
  },
  tableFooterText: {
    fontSize: 16,
    color: theme.color.white,
    fontWeight: "bold",
  },
  tableFooterDropdownText: {
    color: theme.color.white,
    fontWeight: theme.font.weight.bold,
    fontSize: 16,
  },
  paginationControls: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    paddingHorizontal: 16,
    paddingVertical: 10,
  },
  dropdownContainer: {
    flexDirection: "row",
    alignItems: "center",
    width: "auto",
  },
  pageNumberText: {
    color: theme.color.white,
    fontWeight: theme.font.weight.bold,
  },
  paginationButton: {
    marginHorizontal: 4,
    padding: 6,
  },
  emptyFooter: {
    height: 40,
  },
  pageButtons: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
  },
  subtitleText: {
    fontSize: 12,
    color: theme.color.red,
    marginTop: 2,
  },
  actionButtons: {
    marginHorizontal: 4,
  },
  buttonContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
  },
});

function getScrollbarWidth() {
  //Thanks copilot!
  if (typeof document !== "undefined") {
    const div = document.createElement("div");
    div.style.overflow = "scroll";
    div.style.width = "100px";
    div.style.height = "100px";
    document.body.appendChild(div);
    const scrollbarWidth = div.offsetWidth - div.clientWidth;
    document.body.removeChild(div);
    return scrollbarWidth;
  }
}

export default function PaginatableTable(props) {
  const {
    columns,
    data,
    handlePagination,
    pageNumber,
    numberOfPages,
    pageSize,
    sortOptions,
    sortOrder,
    paginatable = true,
  } = props;
  const [scrollBarWidth] = useState(() => getScrollbarWidth());

  const dynamicStyles = StyleSheet.create({
    header: {
      paddingRight: scrollBarWidth,
    },
  });

  /*
   * Table column and data formats:
   *  each column takes a key, title, width, and an buttons[]. Each button can contain an icon, iconSize, onPress, and text
   */
  return (
    <View style={classes.root}>
      {typeof handlePagination === "function" && (
        <View style={classes.tools}>
          <Text style={classes.tableFooterText}>Sort by:</Text>
          <View style={classes.dropdownContainer}>
            <Dropdown
              backgroundColor={theme.color.tableHeader}
              textStyle={classes.tableFooterDropdownText}
              options={
                sortOptions?.filter(
                  (option) =>
                    option && option.label && option.value !== undefined,
                ) || []
              }
              selected={sortOrder}
              onSelect={(value) => handlePagination({ sortOrder: value })}
            />
          </View>
        </View>
      )}
      <View style={[classes.tableHeaderRow, dynamicStyles.header]}>
        {columns.map((column) => (
          <View
            key={column.key}
            style={[
              classes.tableHeaderCell,
              {
                flex: column.flex || 1,
                alignItems: column.alignment
                  ? column.alignment === "left"
                    ? "flex-start"
                    : column.alignment === "right"
                      ? "flex-end"
                      : "center"
                  : "center",
              },
            ]}
          >
            <Text style={classes.tableHeaderCellText}>{column.title}</Text>
          </View>
        ))}
      </View>
      <ScrollView>
        {data.map((row, rowIndex) => (
          <View style={classes.tableBodyRow} key={row.id || rowIndex} testID={`row-${rowIndex}`}>
            {columns.map((column) => (
              <View
                key={column.key}
                style={[
                  classes.tableBodyCell,
                  {
                    flex: column.flex || 1,
                    justifyContent: column.alignment
                      ? column.alignment === "left"
                        ? "flex-start"
                        : column.alignment === "right"
                          ? "flex-end"
                          : "center"
                      : "center",
                  },
                ]}
              >
                {column.buttons?.length > 0 ? (
                  <View style={classes.buttonContainer}>
                    {column.buttons.map((button, index) => {
                      const buttonText =
                        typeof button.text === "function"
                          ? button.text(row)
                          : button.text;

                      if (!buttonText) {
                        return null;
                      }

                      return (
                        <View key={index} style={classes.actionButtons}>
                          <CustomButton
                            key={index}
                            title={buttonText}
                            icon={button.icon}
                            iconSizeOverride={button.iconSize || null}
                            onPress={() => button.onPress(row)}
                            variant={
                              typeof button.variant === "function"
                                ? button.variant(row)
                                : button.variant
                            }
                          />
                        </View>
                      );
                    })}
                  </View>
                ) : (
                  <View style={classes.tableBodyCellTextContainer}>
                    <Text
                      style={[
                        classes.tableBodyCellText,
                        { fontSize: column.fontSize },
                        { color: row[column.key]?.color },
                      ]}
                    >
                      {(() => {
                        const cellData = row[column.key];
                        // 1. Handle Missing Data
                        if (cellData === null || cellData === undefined) {
                          return "";
                        }
                        // 2. Handle Arrays (Bullet Points / Lists)
                        if (Array.isArray(cellData)) {
                          return cellData.join(", ");
                        }
                        // 3. Handle Valid React Elements (Icons, Custom Tags)
                        if (React.isValidElement(cellData)) {
                          return cellData;
                        }
                        // 4. Handle Rich Objects with Style/Value properties
                        if (typeof cellData === "object") {
                          return cellData.value ?? "";
                        }
                        // 5. Handle Primitives (Strings, Numbers)
                        return cellData;
                    })()}
                    </Text>
                    {column.subtitle && (
                      <Text style={classes.subtitleText}>
                        {typeof column.subtitle === "function"
                          ? column.subtitle(row)
                          : column.subtitle}
                      </Text>
                    )}
                  </View>
                )}
              </View>
            ))}
          </View>
        ))}
      </ScrollView>
      <View style={classes.tableFooterRow}>
        {paginatable && typeof handlePagination === "function" ? (
          <View style={classes.paginationControls}>
            <Text style={classes.tableFooterText}>Rows per page:</Text>
            <View style={classes.dropdownContainer}>
              <Dropdown
                backgroundColor={theme.color.tableHeader}
                textStyle={classes.tableFooterDropdownText}
                options={TABLE_PAGE_SIZES.map((size) => ({
                  label: size,
                  value: size,
                }))}
                selected={pageSize}
                onSelect={(value) =>
                  handlePagination({ rowsPerPage: Number(value) })
                }
              />
            </View>
            <View style={classes.pageButtons}>
              <TouchableOpacity
                style={classes.paginationButton}
                onPress={() => handlePagination({ direction: "prev" })}
              >
                <Icon
                  name="chevron-left"
                  size={24}
                  color={theme.color.background}
                />
              </TouchableOpacity>
              <Text style={classes.pageNumberText}>
                {pageNumber}/{numberOfPages}
              </Text>
              <TouchableOpacity
                style={classes.paginationButton}
                onPress={() => handlePagination({ direction: "next" })}
              >
                <Icon
                  name="chevron-right"
                  size={24}
                  color={theme.color.background}
                />
              </TouchableOpacity>
            </View>
          </View>
        ) : (
          <View style={classes.emptyFooter} />
        )}
      </View>
    </View>
  );
}
